import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const LINES = [
  { delay: 0,    text: '> cd ~' },
  { delay: 600,  text: '> ls pages/' },
  { delay: 1200, text: '  home  contact  impressum  privacy' },
  { delay: 1900, text: '> cd this-page' },
  { delay: 2600, text: '  bash: this-page: No such file or directory', error: true },
  { delay: 3200, text: '> cat error.log' },
  { delay: 3800, text: '  [404] Page not found — it never existed or moved.', error: true },
  { delay: 4400, text: '> _', cursor: true },
];

export default function NotFoundPage() {
  const location = useLocation();
  const [visibleLines, setVisibleLines] = useState([]);

  useEffect(() => {
    const timers = LINES.map((line, i) =>
      setTimeout(() => {
        setVisibleLines(prev => [...prev, i]);
      }, line.delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  // Replace placeholder with actual path
  const getLineText = (line) => {
    if (line.text.includes('this-page')) {
      return line.text.replace('this-page', location.pathname.replace('/', '') || 'this-page');
    }
    return line.text;
  };

  return (
    <div className="nf-root">
      <div className="nf-bg-grid" />

      <motion.div
        className="nf-inner"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Big 404 */}
        <div className="nf-hero">
          <span className="nf-code">404</span>
          <span className="nf-slash">&lt;/&gt;</span>
        </div>

        {/* Terminal block */}
        <div className="nf-terminal">
          <div className="nf-terminal-bar">
            <span className="nf-dot nf-dot-red" />
            <span className="nf-dot nf-dot-yellow" />
            <span className="nf-dot nf-dot-green" />
            <span className="nf-terminal-title">bash — portfolio</span>
          </div>
          <div className="nf-terminal-body">
            {LINES.map((line, i) => (
              visibleLines.includes(i) && (
                <motion.div
                  key={i}
                  className={`nf-line${line.error ? ' nf-line-error' : ''}${line.cursor ? ' nf-line-cursor' : ''}`}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {getLineText(line)}
                </motion.div>
              )
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          className="nf-actions"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4.6, duration: 0.5 }}
        >
          <Link to="/" className="btn btn-fill">← Zurück zur Startseite</Link>
          <Link to="/contact" className="btn btn-outline">Kontakt</Link>
        </motion.div>
      </motion.div>

      <style>{`
        .nf-root {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
          padding: 80px 24px 40px;
        }

        .nf-bg-grid {
          position: fixed;
          inset: 0;
          background-image:
            radial-gradient(circle, rgba(251,146,60,0.06) 1px, transparent 1px);
          background-size: 32px 32px;
          pointer-events: none;
          z-index: 0;
        }

        .nf-inner {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 580px;
          display: flex;
          flex-direction: column;
          gap: 40px;
        }

        /* Big number */
        .nf-hero {
          display: flex;
          align-items: baseline;
          gap: 20px;
        }

        .nf-code {
          font-family: 'Inter', sans-serif;
          font-size: clamp(80px, 18vw, 140px);
          font-weight: 800;
          line-height: 1;
          color: transparent;
          -webkit-text-stroke: 2px rgba(245,245,245,0.15);
          letter-spacing: -4px;
          user-select: none;
        }

        .nf-slash {
          font-family: 'Inter', sans-serif;
          font-size: clamp(28px, 6vw, 48px);
          font-weight: 700;
          color: var(--accent, #fb923c);
          letter-spacing: -1px;
        }

        /* Terminal */
        .nf-terminal {
          background: #0d0d0d;
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        }

        .nf-terminal-bar {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 14px;
          background: #161616;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }

        .nf-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }
        .nf-dot-red    { background: #ff5f57; }
        .nf-dot-yellow { background: #febc2e; }
        .nf-dot-green  { background: #28c840; }

        .nf-terminal-title {
          margin-left: 8px;
          font-size: 11px;
          color: rgba(255,255,255,0.35);
          font-family: 'Inter', monospace;
        }

        .nf-terminal-body {
          padding: 20px 20px 24px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-height: 180px;
        }

        .nf-line {
          font-family: 'Courier New', 'Consolas', monospace;
          font-size: 13px;
          color: rgba(245,245,245,0.75);
          line-height: 1.6;
        }

        .nf-line-error {
          color: #f87171;
        }

        .nf-line-cursor {
          color: var(--accent, #fb923c);
          animation: nf-blink 1s step-end infinite;
        }

        @keyframes nf-blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }

        /* Actions */
        .nf-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        @media (max-width: 480px) {
          .nf-actions { flex-direction: column; }
          .nf-actions a { text-align: center; justify-content: center; }
        }
      `}</style>
    </div>
  );
}
